import i18n from '@/lang'
import OrderSelect from '@pt/components/order-select.vue'
import SearchInput from '@pt/components/search-input.vue'
import {
  AccountStatus,
  AdAccount,
  AdAccountAuthQuery,
  AdAuthType,
} from '@pt/api/user/type'
import { SchemaFormItemRule } from '@/types/schema-form.type'
import { channelList } from '@com/utils/channel'
import { EnumToUnion } from 'types'
import { PtStatus } from '@com/pt-components/types/'
import _ from 'lodash'
import { TrafficType } from '@com/enums/channel'

// 授权渠道列表
export const authChannelList = () =>
  channelList.filter(
    (item) =>
      item.value === 21 ||
      item.value === 5 ||
      item.value === 7 ||
      item.value === 10,
  )

// 新增平台类型
export enum PlatformType {
  Xmp = 'xmp',
}

// 因xmp为跳转平台，非渠道，因此新增授权平台
export const platformList = [{ value: PlatformType.Xmp, label: 'Xmp' }]

// 账号授权平台(渠道+平台)
export const authAuthorizeList = () => [...authChannelList(), ...platformList]

// xmp支持的打包渠道
export const xmpPlatformList = () =>
  channelList.filter((item) =>
    [7, 5, 10, 6, 4, 3, 19, 28].some((id) => id === item.value),
  )

export const exchangeListLabel = (value: number | string, list: any[]) => {
  let temp
  if (typeof value === 'number') {
    temp = list.find((item) => item.value === Number(value))
  } else {
    temp = list.find((item) => item.value === value)
  }

  if (temp) {
    return temp.label
  }
  return '-'
}

export const dateOptions: { value: string; label: string }[] = [
  {
    label: `${i18n.t('playTurbo.promoteManagement.promote.table.updatetime')}`,
    value: 'updated_time',
  },
  {
    label: `${i18n.t('playTurbo.videoProject.table.createTime')}`,
    value: 'created_time',
  },
]

export const getAccountStatus = (row: AdAccount) => {
  if (
    // pt授权active且xmp授权active
    row.status === AccountStatus.ACTIVE
  ) {
    return AccountStatus.ACTIVE
  } else if (row.status === AccountStatus.DISABLED) {
    // 任意授权为disabled
    return AccountStatus.DISABLED
  } else if (row.status === AccountStatus.PENDING) {
    // 授权中
    return AccountStatus.PENDING
  }
  // 其他情况未授权
  return AccountStatus.UNBOUND
}
export const getAuthItem = (trafficType: string | TrafficType): string[] => {
  let authItem: string[]
  switch (trafficType) {
    case 21:
      authItem = ['email', 'api_key', 'access_key']
      break
    case 5:
      authItem = [
        'account_name',
        // 'api_key',
        'secret_key',
        'key_id',
        'org_id',
        'org_core_id',
      ]
      break
    case 7:
      authItem = ['account_name', 'report_key', 'client_id', 'client_secret']
      break
    case 10:
      authItem = ['account_name', 'secret_key', 'refresh_token']
      break
    case PlatformType.Xmp:
      authItem = ['client_id', 'client_secret']
      break
    default:
      authItem = []
  }
  return authItem
}

export const getChannelSubmitQuery = (query: AdAccountAuthQuery) => {
  // type为授权渠道,传渠道参数traffic_type;type为授权平台,传平台参数platform_name
  const dynamicParam =
    query.type === AdAuthType.Channel ? 'traffic_type' : 'platform_name'

  return _.pick(query, [
    ...getAuthItem(query.traffic_type),
    ...['type', dynamicParam, 'show_all', 'report_permission'],
  ])
}

export const statusOptions = [
  {
    label: i18n.t('playTurbo.authAccount.authStatus.active'),
    value: AccountStatus.ACTIVE,
  },
  {
    label: i18n.t('playTurbo.authAccount.authStatus.unbound'),
    value: AccountStatus.UNBOUND,
  },
  {
    label: i18n.t('playTurbo.authAccount.authStatus.disabled'),
    value: AccountStatus.DISABLED,
  },
  {
    label: i18n.t('playTurbo.authAccount.authStatus.pending'),
    value: AccountStatus.PENDING,
  },
]

export const reportPermissionOptions = [
  {
    label: i18n.t('playTurbo.authAccount.authDialog.form.dataRange.all'),
    value: 1,
  },
  {
    label: i18n.t('playTurbo.authAccount.authDialog.form.dataRange.part'),
    value: 2,
  },
  {
    label: i18n.t('playTurbo.authAccount.authDialog.form.dataRange.forbidden'),
    value: 3,
  },
]

export const searchFormSchema = () => [
  {
    type: 'object',
    properties: {
      search_field: {
        component: 'select',
        class: 'search-field',
        noRefresh: true, // 禁止刷新路由
        field: {
          options: [
            {
              label: i18n
                .t('playTurbo.authAccount.table.search.email')
                .toString(),
              value: 'email',
            },
            {
              label: i18n
                .t('playTurbo.authAccount.table.search.accountName')
                .toString(),
              value: 'account_name',
            },
          ],
        },
      },
      keyword: {
        component: SearchInput,
        class: 'search-keyword',
        field: {
          placeholder: i18n.t('playTurbo.recordProject.record.filter.search'),
        },
      },
      traffic_type: {
        component: 'select',
        field: {
          placeholder: i18n.t(
            'playTurbo.promoteManagement.promote.filter.platform',
          ),
          clearable: true,
          options: authAuthorizeList(),
        },
      },
      statuses: {
        component: 'select',
        field: {
          checkable: true,
          clearable: true,
          multiple: true,
          'collapse-tags': true,
          placeholder: i18n.t('playTurbo.authAccount.table.search.status'),
          options: statusOptions,
        },
      },
      order: {
        component: OrderSelect,
        field: {
          placeholder: i18n.t('playTurbo.whole.choice'),
          options: [
            {
              label: i18n.t('playTurbo.authAccount.table.search.authTime'),
              value: 'auth_time',
            },
          ],
        },
      },
    },
  },
]

export const exchangePtStatusValue = (
  value: EnumToUnion<typeof AccountStatus>,
) => {
  if (value === AccountStatus.ACTIVE) {
    return PtStatus.Success
  } else if (value === AccountStatus.DISABLED) {
    return PtStatus.Fail
  }
  return PtStatus.Pending
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const validateEmail = (
  rule: SchemaFormItemRule,
  value: string,
  // eslint-disable-next-line @typescript-eslint/ban-types
  callback: Function,
) => {
  // ss 使用 yiminghe async-validator 邮箱校验正则修改
  // eslint-disable-next-line
    const reg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  if (value.trim() === '') {
    callback(new Error(i18n.t('playTurbo.forget.form.mail.required') as string))
  } else if (!reg.test(value)) {
    callback(
      new Error(i18n.t('playTurbo.forget.form.mail.formatVeryfy') as string),
    )
  } else {
    callback()
  }
}
